import { useState } from "react";
import { apiFetch } from "../../api/api";
import image23 from "../../assets/image23.png";
import FormDropdown from "../../components/common/Formdropdown";

const YELLOW = "#FFEB3B";
const DARK = "#1a1a1a";

// Only letters, spaces, apostrophes, hyphens allowed — no digits/symbols
const nameReg = /^[A-Za-z\s'-]+$/;

function validate(fields) {
  const errors = {};
  const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!fields.fname.trim()) {
    errors.fname = "First name is required.";
  } else if (!nameReg.test(fields.fname.trim())) {
    errors.fname = "Name should only contain letters.";
  }

  if (!fields.lname.trim()) {
    errors.lname = "Last name is required.";
  } else if (!nameReg.test(fields.lname.trim())) {
    errors.lname = "Name should only contain letters.";
  }

  if (!emailReg.test(fields.email.trim())) errors.email = "Enter a valid email address.";

  const mobileDigits = fields.mobile.trim();
  if (!mobileDigits) {
    errors.mobile = "Mobile number is required.";
  } else if (!/^[6-9]\d{9}$/.test(mobileDigits)) {
    errors.mobile = "Enter a valid 10-digit mobile number.";
  }

  if (!fields.company.trim()) errors.company = "Company name is required.";
  if (!fields.size) errors.size = "Please select company size.";
  if (!fields.type) errors.type = "Please select an inquiry type.";
  if (fields.msg.trim().length < 20) errors.msg = "Message must be at least 20 characters.";
  if (!fields.agree) errors.agree = "Please accept the terms & conditions.";

  return errors;
}

export default function CorporateInquiry() {
  const [fields, setFields] = useState({
    fname: "", lname: "", email: "", mobile: "", company: "", size: "", type: "", msg: "", agree: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { id, value } = e.target;
    const cleanValue = id === "mobile" ? value.replace(/\D/g, "") : value;
    setFields((prev) => ({ ...prev, [id]: cleanValue }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleDropdownChange = (id, value) => {
    setFields((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleCheckbox = (e) => {
    const { checked } = e.target;
    setFields((prev) => ({ ...prev, agree: checked }));
    if (errors.agree) setErrors((prev) => ({ ...prev, agree: "" }));
  };

 const handleSubmit = async () => {
  const errs = validate(fields);
  if (Object.keys(errs).length > 0) {
    setErrors(errs);
    return;
  }
  setSubmitError("");
  setSubmitting(true);
  try {
    await apiFetch("/inquiries/submit.php", {
      method: "POST",
      auth: false,
      body: {
        firstName: fields.fname.trim(),
        lastName: fields.lname.trim(),
        email: fields.email.trim(),
        mobile: fields.mobile.trim(),
        company: fields.company.trim(),
        companySize: fields.size,
        inquiryType: fields.type,
        message: fields.msg.trim(),
        source: "corporate_inquiry_home",
      },
    });
    setSubmitted(true);

    // Google Ads conversion tracking
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-18471785571/CONVERSION_LABEL'
      });
    }
  } catch (err) {
    setSubmitError(err.message || "Something went wrong. Please try again.");
  } finally {
    setSubmitting(false);
  }
};

  
  const handleReset = () => {
    setFields({ fname: "", lname: "", email: "", mobile: "", company: "", size: "", type: "", msg: "", agree: false });
    setErrors({});
    setSubmitted(false);
  };

  const inputCls = (id) =>
    `w-full px-3 py-2.5 sm:py-2 rounded-lg text-[15px] sm:text-sm font-sans border transition-all outline-none bg-white text-[#1a1a1a] placeholder-gray-500 ${
      errors[id] ? "border-red-500 focus:border-red-500" : "border-black/20 focus:border-[#1a1a1a]"
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center p-0 sm:p-4">
      <div
        className="w-full max-w-7xl rounded-none sm:rounded-2xl overflow-hidden border-0 sm:border sm:border-black/10 grid grid-cols-1 md:grid-cols-2 md:min-h-[560px]"
      >
        {/* LEFT — Image */}
        <div className="relative overflow-hidden h-[200px] xs:h-[240px] sm:h-[300px] md:h-auto md:min-h-[300px]">
          <img
            src={image23}
            alt="Team collaborating in a modern office"
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0 flex flex-col justify-end p-4 xs:p-5 sm:p-6 md:p-8"
            style={{ background: "rgba(0,0,0,0.35)" }}
          >
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] xs:text-xs font-medium mb-2 xs:mb-3 md:mb-4 w-fit"
              style={{ background: YELLOW, color: DARK }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Corporate Inquiries
            </div>
            <h1 className="text-xl xs:text-2xl sm:text-3xl font-medium leading-tight text-white" style={{ fontFamily: "Georgia, serif" }}>
              Partner with us today
            </h1>
            <p className="text-[12.5px] xs:text-sm text-white/85 mt-1.5 xs:mt-2 leading-relaxed hidden xs:block">
              Enterprise solutions crafted for businesses of all sizes.
            </p>
            <div className="mt-2.5 xs:mt-3 md:mt-4 flex flex-col gap-1.5 xs:gap-2">
              {[
                { icon: "⏱", text: "Response within 24 hours" },
                { icon: "🔒", text: "Confidential & secure" },
                { icon: "👥", text: "Dedicated account manager" },
              ].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-white/90 text-[12px] xs:text-sm">
                  <span>{icon}</span> {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Yellow form */}
        <div
          className="flex flex-col justify-center px-4 py-6 xs:px-5 sm:px-7 sm:py-8"
          style={{ background: YELLOW }}
        >
          {!submitted ? (
            <>
              <div className="flex items-center gap-2 text-[15px] xs:text-base font-medium mb-4 xs:mb-5" style={{ color: DARK }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M2 7l10 7 10-7" />
                </svg>
                Send an inquiry
              </div>

              {/* Row: fname + lname */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-3">
                {[
                  { id: "fname", label: "First name", placeholder: "Rahul" },
                  { id: "lname", label: "Last name", placeholder: "Sharma" },
                ].map(({ id, label, placeholder }) => (
                  <div key={id}>
                    <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                      {label} <span className="text-red-600">*</span>
                    </label>
                    <input
                      id={id}
                      type="text"
                      placeholder={placeholder}
                      value={fields[id]}
                      onChange={handleChange}
                      className={inputCls(id)}
                    />
                    {errors[id] && <p className="text-red-600 text-xs mt-1">{errors[id]}</p>}
                  </div>
                ))}
              </div>

              {/* Email + Mobile */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2.5 sm:gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                    Work email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    placeholder="rahul@company.com"
                    value={fields.email}
                    onChange={handleChange}
                    className={inputCls("email")}
                  />
                  {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                    Mobile number <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    maxLength={10}
                    value={fields.mobile}
                    onChange={handleChange}
                    className={inputCls("mobile")}
                  />
                  {errors.mobile && <p className="text-red-600 text-xs mt-1">{errors.mobile}</p>}
                </div>
              </div>

              {/* Company + Size */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2.5 sm:gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                    Company <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="company"
                    type="text"
                    placeholder="Acme Corp"
                    value={fields.company}
                    onChange={handleChange}
                    className={inputCls("company")}
                  />
                  {errors.company && <p className="text-red-600 text-xs mt-1">{errors.company}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                    Company size <span className="text-red-600">*</span>
                  </label>
                  <FormDropdown
                    options={["1 – 10", "11 – 50", "51 – 200", "201 – 1000", "1000+"]}
                    value={fields.size}
                    onChange={(val) => handleDropdownChange("size", val)}
                    placeholder="Select..."
                    error={!!errors.size}
                  />
                  {errors.size && <p className="text-red-600 text-xs mt-1">{errors.size}</p>}
                </div>
              </div>

              {/* Inquiry type */}
              <div className="mb-3">
                <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                  Inquiry type <span className="text-red-600">*</span>
                </label>
                <FormDropdown
                  options={["Partnership", "Licensing", "Enterprise sales", "Media / Press", "Other"]}
                  value={fields.type}
                  onChange={(val) => handleDropdownChange("type", val)}
                  placeholder="Select type..."
                  error={!!errors.type}
                />
                {errors.type && <p className="text-red-600 text-xs mt-1">{errors.type}</p>}
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-xs font-medium mb-1" style={{ color: "#333" }}>
                  Message <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="msg"
                  placeholder="Tell us about your needs..."
                  maxLength={500}
                  value={fields.msg}
                  onChange={handleChange}
                  rows={3}
                  className={inputCls("msg") + " resize-y"}
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.msg ? (
                    <p className="text-red-600 text-xs">{errors.msg}</p>
                  ) : (
                    <span />
                  )}
                <span className="text-xs text-gray-800 ml-auto">{fields.msg.length} / 500</span>
                </div>
              </div>

              {/* Terms & Conditions checkbox */}
              <div className="mb-4">
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    id="agree"
                    type="checkbox"
                    checked={fields.agree}
                    onChange={handleCheckbox}
                    className="mt-0.5 w-4 h-4 accent-[#1a1a1a] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs leading-snug" style={{ color: "#333" }}>
                    I agree to the{" "}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: DARK }}>
                      Terms &amp; Conditions
                    </a>
                    . <span className="text-red-600">*</span>
                  </span>
                </label>
                {errors.agree && <p className="text-red-600 text-xs mt-1">{errors.agree}</p>}
              </div>

              {submitError && (
                <p className="text-red-600 text-xs mb-3 text-center">{submitError}</p>
              )}

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 sm:py-2.5 rounded-lg text-[15px] sm:text-sm font-medium transition-opacity hover:opacity-80 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: DARK, color: YELLOW }}
              >
                {submitting ? (
                  "Sending..."
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Submit inquiry
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 text-center py-8">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: DARK }}
              >
                <svg width="26" height="26" fill="none" stroke={YELLOW} strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="text-lg font-medium" style={{ color: DARK }}>Inquiry received!</h3>
              <p className="text-sm" style={{ color: "#333" }}>
                Our team will get back to you within 24 hours.
              </p>
              <button
                onClick={handleReset}
                className="mt-2 px-5 py-2.5 rounded-lg text-sm border transition-opacity hover:opacity-70 active:scale-95"
                style={{ borderColor: DARK, color: DARK, background: "transparent" }}
              >
                Send another
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}